import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';

const Header: React.FC = () => {
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.products'), href: '#products' },
    { name: t('nav.stories'), href: '#stories' },
    { name: t('nav.contact'), href: '#waitlist' },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-parchment/95 backdrop-blur-md shadow-sm border-b border-linen'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Brand */}
          <motion.a
            href="#home"
            whileHover={{ opacity: 0.85 }}
          >
            <Logo size="md" showWordmark={true} />
          </motion.a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.08 }}
                className="relative text-sm font-medium text-charcoal/80 hover:text-herb-green transition-colors duration-200 group"
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-px bg-herb-green transition-all duration-300 group-hover:w-full" />
              </motion.a>
            ))}
            <motion.a
              href="#waitlist"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="px-5 py-2 bg-herb-green text-white text-sm font-semibold rounded-full hover:bg-forest transition-colors duration-200"
            >
              Get Early Access
            </motion.a>
          </nav>

          {/* Mobile menu button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-linen transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-5 h-5 text-forest" />
            ) : (
              <Menu className="w-5 h-5 text-forest" />
            )}
          </motion.button>
        </div>

        {/* Mobile nav */}
        {isMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4 pt-4 border-t border-linen space-y-1"
          >
            {navItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.07 }}
                onClick={() => setIsMenuOpen(false)}
                className="block py-2.5 px-2 text-charcoal/80 hover:text-herb-green font-medium transition-colors"
              >
                {item.name}
              </motion.a>
            ))}
            <a
              href="#waitlist"
              onClick={() => setIsMenuOpen(false)}
              className="block mt-3 px-4 py-2.5 bg-herb-green text-white text-sm font-semibold rounded-full text-center"
            >
              Get Early Access
            </a>
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
